import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface VideoPdfItem {
  id: string;
  title: string;
  description?: string | null;
  file_url: string;
  file_type: string;
  mime_type?: string | null;
  file_size?: number | null;
  display_order?: number | null;
}

/**
 * Fetches all video → PDF/image attachments and returns:
 * - byVideoId: Map<videoId, VideoPdfItem[]> (sorted by display_order)
 * - hasPdfSet: Set<videoId> for quick "this video has PDFs" checks
 */
export const useVideoPdfAttachments = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["video-pdf-attachments-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("video_pdf_attachments")
        .select(
          "video_id, display_order, pdf_library(id, title, description, file_url, file_type, mime_type, file_size)",
        )
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    staleTime: 60_000,
  });

  return useMemo(() => {
    const byVideoId = new Map<string, VideoPdfItem[]>();
    const hasPdfSet = new Set<string>();
    (data || []).forEach((row: any) => {
      const pdf = row.pdf_library;
      if (!pdf) return;
      const item: VideoPdfItem = {
        id: pdf.id,
        title: pdf.title,
        description: pdf.description,
        file_url: pdf.file_url,
        file_type: pdf.file_type,
        mime_type: pdf.mime_type,
        file_size: pdf.file_size,
        display_order: row.display_order,
      };
      const arr = byVideoId.get(row.video_id) || [];
      arr.push(item);
      byVideoId.set(row.video_id, arr);
      hasPdfSet.add(row.video_id);
    });
    return { byVideoId, hasPdfSet, isLoading };
  }, [data, isLoading]);
};
